import React, { useState, useEffect, useCallback } from 'react';
import { VarianceAppProvider, useVarianceAppContext } from './VarianceAppContext';
import ModernLayout from './ModernLayout';
import HistoricalTrend from './HistoricalTrend';
import ModernPeerComparison from './ModernPeerComparison';
import ModernLineItemAnalysis from './ModernLineItemAnalysis';
import BusinessSegmentFilter from './BusinessSegmentFilter';
import { Clock, TrendingUp, Calendar, Filter, Banknote, PieChart, BarChart3, X, Users, Table, Settings } from 'lucide-react';
import axios from 'axios';
import './ModernVarianceAnalysis.css';
import config, { getDefaultHeaders } from '../config/api';
import { useCountry } from '../context/CountryContext';

const ModernVarianceAnalysisContent = () => {
  const { country } = useCountry();
  const { selectedBusinessSegment, selectedFinancialItems, dataAvailable } = useVarianceAppContext();
  const [isLoading, setIsLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Historical Trend filter states (moved from HistoricalTrend)
  const [selectedBank, setSelectedBank] = useState('');
  const [selectedPeriodType, setSelectedPeriodType] = useState('');
  const [selectedCurrentPeriod, setSelectedCurrentPeriod] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedFinancialItemsLocal, setSelectedFinancialItemsLocal] = useState([]);

  // Peer Comparison filter states
  const [peerSelectedBanks, setPeerSelectedBanks] = useState([]);
  const [peerSelectedPeriodType, setPeerSelectedPeriodType] = useState('');
  const [peerSelectedCurrentPeriod, setPeerSelectedCurrentPeriod] = useState('');
  const [peerSelectedCategory, setPeerSelectedCategory] = useState('');
  const [peerSelectedItems, setPeerSelectedItems] = useState([]);
  const [peerSelectedViewType, setPeerSelectedViewType] = useState('actual');

  // Line Item Analysis filter states
  const [lineItemSelectedBank, setLineItemSelectedBank] = useState('');
  const [lineItemSelectedCategory, setLineItemSelectedCategory] = useState('');
  const [lineItemSelectedViewType, setLineItemSelectedViewType] = useState('actual');
  const [lineItemSelectedPeriodType, setLineItemSelectedPeriodType] = useState('');
  const [lineItemSelectedCurrentPeriod, setLineItemSelectedCurrentPeriod] = useState('');
  const [lineItemSelectedPreviousPeriod, setLineItemSelectedPreviousPeriod] = useState('');
  const [lineItemSelectedColumns, setLineItemSelectedColumns] = useState(['line_item', 'current', 'previous', 'variance_amount', 'variance_percent', 'segment', 'ai_explanation']);
  const [showLineItemColumnSelector, setShowLineItemColumnSelector] = useState(false);

  // Data arrays
  const [banks, setBanks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [financialItems, setFinancialItems] = useState([]);
  const [financialItemsLoading, setFinancialItemsLoading] = useState(false);
  
  // Separate financial items for each component
  const [peerFinancialItems, setPeerFinancialItems] = useState([]);
  const [peerFinancialItemsLoading, setPeerFinancialItemsLoading] = useState(false);
  const [lineItemFinancialItems, setLineItemFinancialItems] = useState([]);
  const [lineItemFinancialItemsLoading, setLineItemFinancialItemsLoading] = useState(false);

  // Period type options
  const periodTypes = [
    { value: 'quarter', label: 'Quarter', icon: Calendar },
    { value: 'half_yearly', label: 'Half Yearly', icon: Calendar },
    { value: '9months', label: '9 Months', icon: Calendar },
    { value: 'yearly', label: 'Yearly', icon: Calendar }
  ];

  // Column configuration for Line Item Analysis
  const columnConfig = {
    line_item: { label: 'LINE ITEM', color: 'purple' },
    current: { label: 'CURRENT', color: 'green' },
    previous: { label: 'PREVIOUS', color: 'blue' },
    variance_amount: { label: 'VARIANCE ($)', color: 'orange' },
    variance_percent: { label: 'VARIANCE (%)', color: 'orange' },
    segment: { label: 'SEGMENT', color: 'blue' },
    ai_explanation: { label: 'AI EXPLANATION', color: 'pink' }
  };


  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);

    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => {
      clearTimeout(timer);
      clearInterval(timeInterval);
    };
  }, []);

  // Define all fetch functions first (before useEffect hooks that use them)
  const fetchBanks = useCallback(async () => {
    if (!selectedBusinessSegment) return;
    try {
      const response = await axios.get(`${config.apiUrl}/banks`, {
        params: { business_segment: selectedBusinessSegment },
        headers: getDefaultHeaders(country)
      });
      setBanks(response.data?.banks || []);
    } catch (err) {
      console.error('Error fetching banks:', err);
      setBanks([]);
    }
  }, [selectedBusinessSegment, country]);

  const fetchCategories = useCallback(async () => {
    if (!selectedBusinessSegment) return;
    try {
      const response = await axios.get(`${config.apiUrl}/categories`, {
        params: { business_segment: selectedBusinessSegment },
        headers: getDefaultHeaders(country)
      });
      setCategories(response.data?.categories || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setCategories([]);
    }
  }, [selectedBusinessSegment, country]);

  const fetchFinancialItems = useCallback(async () => {
    if (!selectedBusinessSegment || !selectedBank || !selectedCategory) return;
    try {
      setFinancialItemsLoading(true);
      const response = await axios.get(`${config.apiUrl}/financial-items`, {
        params: {
          business_segment: selectedBusinessSegment,
          category: selectedCategory,
          bank: selectedBank,
          view_type: 'actual'
        },
        headers: getDefaultHeaders(country)
      });
      setFinancialItems(response.data?.financial_items || []);
    } catch (err) {
      console.error('Error fetching financial items:', err);
      setFinancialItems([]);
    } finally {
      setFinancialItemsLoading(false);
    }
  }, [selectedBusinessSegment, selectedCategory, selectedBank, country]);

  const fetchPeerFinancialItems = useCallback(async () => {
    if (!selectedBusinessSegment || !peerSelectedCategory || peerSelectedBanks.length === 0) return;
    try {
      setPeerFinancialItemsLoading(true);
      const response = await axios.get(`${config.apiUrl}/financial-items`, {
        params: {
          business_segment: selectedBusinessSegment,
          category: peerSelectedCategory,
          bank: peerSelectedBanks[0], // Use first selected bank for fetching items
          view_type: 'actual'
        },
        headers: getDefaultHeaders(country)
      });
      setPeerFinancialItems(response.data?.financial_items || []);
    } catch (err) {
      console.error('Error fetching peer financial items:', err);
      setPeerFinancialItems([]);
    } finally {
      setPeerFinancialItemsLoading(false);
    }
  }, [selectedBusinessSegment, peerSelectedCategory, peerSelectedBanks, country]);

  const fetchLineItemFinancialItems = useCallback(async () => {
    if (!selectedBusinessSegment || !lineItemSelectedCategory || !lineItemSelectedBank) return;
    try {
      setLineItemFinancialItemsLoading(true);
      const response = await axios.get(`${config.apiUrl}/financial-items`, {
        params: {
          business_segment: selectedBusinessSegment,
          category: lineItemSelectedCategory,
          bank: lineItemSelectedBank,
          view_type: 'actual'
        },
        headers: getDefaultHeaders(country)
      });
      setLineItemFinancialItems(response.data?.financial_items || []);
    } catch (err) {
      console.error('Error fetching line item financial items:', err);
      setLineItemFinancialItems([]);
    } finally {
      setLineItemFinancialItemsLoading(false);
    }
  }, [selectedBusinessSegment, lineItemSelectedCategory, lineItemSelectedBank, country]);

  // Fetch data when selections change (moved from HistoricalTrend) - defined after functions
  useEffect(() => {
    if (selectedBusinessSegment) {
      fetchBanks();
      fetchCategories();
    }
  }, [selectedBusinessSegment, fetchBanks, fetchCategories]);

  useEffect(() => {
    if (selectedBusinessSegment && selectedBank && selectedCategory) {
      fetchFinancialItems();
    }
  }, [selectedBusinessSegment, selectedBank, selectedCategory, fetchFinancialItems]);

  // Fetch financial items for Peer Comparison when category and banks are selected
  useEffect(() => {
    if (selectedBusinessSegment && peerSelectedCategory && peerSelectedBanks.length > 0) {
      fetchPeerFinancialItems();
    } else {
      setPeerFinancialItems([]);
    }
  }, [selectedBusinessSegment, peerSelectedCategory, peerSelectedBanks.join(','), fetchPeerFinancialItems]);

  // Fetch financial items for Line Item Analysis when category and bank are selected
  useEffect(() => {
    if (selectedBusinessSegment && lineItemSelectedCategory && lineItemSelectedBank) {
      fetchLineItemFinancialItems();
    } else {
      setLineItemFinancialItems([]);
    }
  }, [selectedBusinessSegment, lineItemSelectedBank, lineItemSelectedCategory, fetchLineItemFinancialItems]);

  const getCurrentPeriodOptions = () => {
    if (!selectedPeriodType) return [];
    
    const currentYear = new Date().getFullYear();
    const options = [];
    
    if (selectedPeriodType === 'quarter') {
      for (let year = currentYear; year >= currentYear - 2; year--) {
        for (let quarter = 4; quarter >= 1; quarter--) {
          options.push({ value: `q${quarter}_${year}`, label: `Q${quarter} ${year}` });
        }
      }
    } else if (selectedPeriodType === 'half_yearly') {
      for (let year = currentYear; year >= currentYear - 2; year--) {
        for (let half = 2; half >= 1; half--) {
          options.push({ value: `h${half}_${year}`, label: `H${half} ${year}` });
        }
      }
    } else if (selectedPeriodType === '9months') {
      for (let year = currentYear; year >= currentYear - 2; year--) {
        options.push({ value: `9m_${year}`, label: `9M ${year}` });
      }
    } else if (selectedPeriodType === 'yearly') {
      for (let year = currentYear; year >= currentYear - 2; year--) {
        options.push({ value: `fy_${year}`, label: `FY ${year}` });
      }
    }
    
    return options;
  };

  const getPeerCurrentPeriodOptions = () => {
    if (!peerSelectedPeriodType) return [];
    
    const currentYear = new Date().getFullYear();
    const options = [];
    
    if (peerSelectedPeriodType === 'quarter') {
      for (let year = currentYear; year >= currentYear - 2; year--) {
        for (let quarter = 4; quarter >= 1; quarter--) {
          options.push({ value: `q${quarter}_${year}`, label: `Q${quarter} ${year}` });
        }
      }
    } else if (peerSelectedPeriodType === 'half_yearly') {
      for (let year = currentYear; year >= currentYear - 2; year--) {
        for (let half = 2; half >= 1; half--) {
          options.push({ value: `h${half}_${year}`, label: `H${half} ${year}` });
        }
      }
    } else if (peerSelectedPeriodType === '9months') {
      for (let year = currentYear; year >= currentYear - 2; year--) {
        options.push({ value: `9m_${year}`, label: `9M ${year}` });
      }
    } else if (peerSelectedPeriodType === 'yearly') {
      for (let year = currentYear; year >= currentYear - 2; year--) {
        options.push({ value: `fy_${year}`, label: `FY ${year}` });
      }
    }
    
    return options;
  };

  const getLineItemCurrentPeriodOptions = () => {
    if (!lineItemSelectedPeriodType) return [];
    
    const currentYear = new Date().getFullYear();
    const options = [];
    
    if (lineItemSelectedPeriodType === 'quarter') {
      for (let year = currentYear; year >= currentYear - 2; year--) {
        for (let quarter = 4; quarter >= 1; quarter--) {
          options.push({ value: `q${quarter}_${year}`, label: `Q${quarter} ${year}` });
        }
      }
    } else if (lineItemSelectedPeriodType === 'half_yearly') {
      for (let year = currentYear; year >= currentYear - 2; year--) {
        for (let half = 2; half >= 1; half--) {
          options.push({ value: `h${half}_${year}`, label: `H${half} ${year}` });
        }
      }
    } else if (lineItemSelectedPeriodType === '9months') {
      for (let year = currentYear; year >= currentYear - 2; year--) {
        options.push({ value: `9m_${year}`, label: `9M ${year}` });
      }
    } else if (lineItemSelectedPeriodType === 'yearly') {
      for (let year = currentYear; year >= currentYear - 2; year--) {
        options.push({ value: `fy_${year}`, label: `FY ${year}` });
      }
    }
    
    return options;
  };

  const handleFinancialItemToggle = (itemId) => {
    setSelectedFinancialItemsLocal(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  const handleUnselectFinancialItem = (itemId) => {
    setSelectedFinancialItemsLocal(prev => prev.filter(id => id !== itemId));
  };

  const clearAllFilters = () => {
    setSelectedBank('');
    setSelectedPeriodType('');
    setSelectedCurrentPeriod('');
    setSelectedCategory('');
    setSelectedFinancialItemsLocal([]);
  };

  // Peer Comparison filter functions
  const handlePeerBankToggle = (bank) => {
    setPeerSelectedBanks(prev => 
      prev.includes(bank) 
        ? prev.filter(b => b !== bank)
        : [...prev, bank]
    );
  };

  const handlePeerItemToggle = (itemId) => {
    setPeerSelectedItems(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  const clearPeerFilters = () => {
    setPeerSelectedBanks([]);
    setPeerSelectedPeriodType('');
    setPeerSelectedCurrentPeriod('');
    setPeerSelectedCategory('');
    setPeerSelectedItems([]);
  };

  // Line Item Analysis filter functions
  const clearLineItemFilters = () => {
    setLineItemSelectedBank('');
    setLineItemSelectedCategory('');
    setLineItemSelectedPeriodType('');
    setLineItemSelectedCurrentPeriod('');
    setLineItemSelectedPreviousPeriod('');
    setLineItemSelectedColumns(['line_item', 'current', 'previous', 'variance_amount', 'variance_percent', 'segment', 'ai_explanation']);
  };

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.filter-group')) {
        setShowLineItemColumnSelector(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatDate = (date) => {
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };


  // Left Panel Content
  const leftPanel = (
    <div className="variance-left-panel">
      {/* Panel Header */}
      <div className="panel-header">
        <div className="panel-title">Variance Analysis</div>
        <div className="panel-description">Comprehensive financial metrics analysis with AI-powered insights</div>
      </div>

      {/* Coming Soon Message */}
      {!dataAvailable && (
        <div style={{ 
          padding: '40px 20px', 
          textAlign: 'center', 
          color: '#6b7280',
          background: '#f9fafb',
          borderRadius: '8px',
          margin: '20px 0'
        }}>
          <TrendingUp size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
          <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px', color: '#374151' }}>
            Coming Soon
          </h3>
          <p style={{ fontSize: '14px', lineHeight: '1.5' }}>
            Variance analysis data is not yet available for this country. Please check back later.
          </p>
        </div>
      )}

      {/* Historical Trend Analysis Section */}
      {dataAvailable && (
      <div className="component-section">
        <div className="component-header">
          <div className="component-title">
            <TrendingUp className="component-icon" />
            Historical Trend Analysis
          </div>
          <div className="component-subtitle">Track financial metrics over time periods</div>
        </div>

        {/* Period Selection Section */}
        <div className="period-selection">
          <div className="period-selection-content">
            <div className="period-sections">
              <div className="period-type-section">
                <label className="period-type-label">
                  <Calendar size={12} />
                  <span>Period Type</span>
                </label>
                <div className="period-type-options">
                  {periodTypes.map(type => {
                    const IconComponent = type.icon;
                    const selected = selectedPeriodType === type.value;
                    return (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => {
                          setSelectedPeriodType(type.value);
                          setSelectedCurrentPeriod('');
                        }}
                        className={`period-type-option ${selected ? 'selected' : ''}`}
                      >
                        <IconComponent size={12} />
                        <span>{type.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="current-period-section">
                <label className="current-period-label">
                  <Calendar size={12} />
                  <span>Current Period</span>
                </label>
                <div className="period-buttons-grid">
                  {getCurrentPeriodOptions().map(option => {
                    const selected = selectedCurrentPeriod === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setSelectedCurrentPeriod(option.value)}
                        disabled={!selectedPeriodType}
                        className={`period-button ${selected ? 'selected' : ''} ${!selectedPeriodType ? 'disabled' : ''}`}
                      >
                        <Calendar size={12} />
                        <span>{option.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Analysis Filters Section */}
        <div className="analysis-filters">
          <div className="analysis-filters-content">
            <div className="filters-grid">
              {/* Bank Filter */}
              <div className="filter-group">
                <label className="filter-label">
                  <Banknote size={12} />
                  <span>Bank</span>
                </label>
                <div className="filter-options">
                  {banks.map(bank => {
                    const selected = selectedBank === bank;
                    return (
                      <button
                        key={bank}
                        type="button"
                        onClick={() => setSelectedBank(bank)}
                        className={`filter-option ${selected ? 'selected' : ''}`}
                      >
                        {bank}
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {/* Category Filter */}
              <div className="filter-group">
                <label className="filter-label">
                  <PieChart size={12} />
                  <span>Category</span>
                </label>
                <div className="filter-options">
                  {categories.map(category => {
                    const selected = selectedCategory === category;
                    return (
                      <button
                        key={category}
                        type="button"
                        onClick={() => setSelectedCategory(category)}
                        className={`filter-option ${selected ? 'selected' : ''}`}
                      >
                        {category}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Financial Items Filter */}
              <div className="financial-items-section">
                <label className="filter-label">
                  <BarChart3 size={12} />
                  <span>Financial Items</span>
                  {selectedFinancialItemsLocal.length > 0 && (
                    <span className="item-count-badge">
                      {selectedFinancialItemsLocal.length}
                    </span>
                  )}
                </label>
                {financialItemsLoading ? (
                  <div className="loading-container">
                    <div className="loading-spinner"></div>
                  </div>
                ) : financialItems.length === 0 ? (
                  <div className="no-items-message">
                    No items found
                  </div>
                ) : (
                  <div className="financial-items-dropdown">
                    <div className="financial-items-list">
                      {financialItems.map(item => {
                        const selected = selectedFinancialItemsLocal.includes(item.id);
                        const level = parseInt(item.level) || 1;
                        const indentClass = `level-${level}`;
                        return (
                          <div
                            key={item.id}
                            className={`financial-item ${selected ? 'selected' : ''} ${indentClass}`}
                            onClick={() => handleFinancialItemToggle(item.id)}
                          >
                            <div className="financial-item-info">
                              <div className={`financial-item-indicator ${selected ? 'selected' : ''}`}></div>
                              <span className="financial-item-name">{item.Item}</span>
                            </div>
                            <span className="level-badge">
                              L{item.level}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Peer Comparison Section */}
      {dataAvailable && (
      <div className="component-section">
        <div className="component-header">
          <div className="component-title">
            <Users className="component-icon" size={16} />
            Peer Comparison Analysis
          </div>
          <div className="component-subtitle">Compare financial metrics across multiple banks</div>
        </div>

        {/* Peer Comparison Period Selection */}
        <div className="period-selection">
          <div className="period-selection-content">
            <div className="period-sections">
              <div className="period-type-section">
                <label className="period-type-label">
                  <Calendar size={12} />
                  <span>Period Type</span>
                </label>
                <div className="period-type-options">
                  {periodTypes.map(type => {
                    const IconComponent = type.icon;
                    const selected = peerSelectedPeriodType === type.value;
                    return (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => {
                          setPeerSelectedPeriodType(type.value);
                          setPeerSelectedCurrentPeriod('');
                        }}
                        className={`period-type-option ${selected ? 'selected' : ''}`}
                      >
                        <IconComponent size={12} />
                        <span>{type.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="current-period-section">
                <label className="current-period-label">
                  <Calendar size={12} />
                  <span>Current Period</span>
                </label>
                <div className="period-buttons-grid">
                  {getPeerCurrentPeriodOptions().map(option => {
                    const selected = peerSelectedCurrentPeriod === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setPeerSelectedCurrentPeriod(option.value)}
                        disabled={!peerSelectedPeriodType}
                        className={`period-button ${selected ? 'selected' : ''} ${!peerSelectedPeriodType ? 'disabled' : ''}`}
                      >
                        <Calendar size={12} />
                        <span>{option.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Peer Comparison Analysis Filters */}
        <div className="analysis-filters">
          <div className="analysis-filters-content">
            <div className="filters-grid">
              {/* Bank Filter - Multi-select */}
              <div className="filter-group">
                <label className="filter-label">
                  <Banknote size={12} />
                  <span>Banks</span>
                  {peerSelectedBanks.length > 0 && (
                    <span className="item-count-badge">
                      {peerSelectedBanks.length}
                    </span>
                  )}
                </label>
                <div className="filter-options">
                  {banks.map(bank => {
                    const selected = peerSelectedBanks.includes(bank);
                    return (
                      <button
                        key={bank}
                        type="button"
                        onClick={() => handlePeerBankToggle(bank)}
                        className={`filter-option ${selected ? 'selected' : ''}`}
                      >
                        {bank}
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {/* Category Filter */}
              <div className="filter-group">
                <label className="filter-label">
                  <PieChart size={12} />
                  <span>Category</span>
                </label>
                <div className="filter-options">
                  {categories.map(category => {
                    const selected = peerSelectedCategory === category;
                    return (
                      <button
                        key={category}
                        type="button"
                        onClick={() => setPeerSelectedCategory(category)}
                        className={`filter-option ${selected ? 'selected' : ''}`}
                      >
                        {category}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Financial Items Filter */}
              <div className="financial-items-section">
                <label className="filter-label">
                  <BarChart3 size={12} />
                  <span>Financial Items</span>
                  {peerSelectedItems.length > 0 && (
                    <span className="item-count-badge">
                      {peerSelectedItems.length}
                    </span>
                  )}
                </label>
                {peerFinancialItemsLoading ? (
                  <div className="loading-container">
                    <div className="loading-spinner"></div>
                  </div>
                ) : peerFinancialItems.length === 0 ? (
                  <div className="no-items-message">
                    No items found
                  </div>
                ) : (
                  <div className="financial-items-dropdown">
                    <div className="financial-items-list">
                      {peerFinancialItems.map(item => {
                        const selected = peerSelectedItems.includes(item.id);
                        const level = parseInt(item.level) || 1;
                        const indentClass = `level-${level}`;
                        return (
                          <div
                            key={item.id}
                            className={`financial-item ${selected ? 'selected' : ''} ${indentClass}`}
                            onClick={() => handlePeerItemToggle(item.id)}
                          >
                            <div className="financial-item-info">
                              <div className={`financial-item-indicator ${selected ? 'selected' : ''}`}></div>
                              <span className="financial-item-name">{item.Item}</span>
                            </div>
                            <span className="level-badge">
                              L{item.level}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Line Item Analysis Section */}
      {dataAvailable && (
      <div className="component-section">
        <div className="component-header">
          <div className="component-title">
            <Table className="component-icon" size={16} />
            Line Item Analysis
          </div>
          <div className="component-subtitle">Hierarchical view of financial items with detailed breakdowns</div>
        </div>

        {/* Line Item Analysis Filters */}
        <div className="analysis-filters">
          <div className="analysis-filters-content">
            <div className="filters-grid">
              {/* Columns Filter */}
              <div className="filter-group">
                <button
                  className="filter-button"
                  onClick={() => setShowLineItemColumnSelector(!showLineItemColumnSelector)}
                >
                  <Settings size={12} />
                  Columns
                </button>
                {showLineItemColumnSelector && (
                  <div className="dropdown-menu">
                    <div className="dropdown-header">
                      <span>Select Columns</span>
                      <button onClick={() => setShowLineItemColumnSelector(false)}>
                        <X size={12} />
                      </button>
                    </div>
                    <div className="dropdown-content">
                      {Object.entries(columnConfig).map(([key, config]) => (
                        <label key={key} className="checkbox-item">
                          <input
                            type="checkbox"
                            checked={lineItemSelectedColumns.includes(key)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setLineItemSelectedColumns([...lineItemSelectedColumns, key]);
                              } else {
                                setLineItemSelectedColumns(lineItemSelectedColumns.filter(col => col !== key));
                              }
                            }}
                          />
                          <span className={`checkbox-label ${config.color}`}>
                            {config.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Category Filter */}
              <div className="filter-group">
                <label className="filter-label">
                  <PieChart size={12} />
                  <span>Category</span>
                </label>
                <select
                  value={lineItemSelectedCategory}
                  onChange={(e) => setLineItemSelectedCategory(e.target.value)}
                  className="filter-select"
                >
                  <option value="">Select Category</option>
                  {categories.map(category => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              {/* Period Type Filter */}
              <div className="filter-group">
                <label className="filter-label">
                  <Calendar size={12} />
                  <span>Period Type</span>
                </label>
                <select
                  value={lineItemSelectedPeriodType}
                  onChange={(e) => {
                    setLineItemSelectedPeriodType(e.target.value);
                    setLineItemSelectedCurrentPeriod('');
                    setLineItemSelectedPreviousPeriod('');
                  }}
                  className="filter-select"
                >
                  <option value="">Select Period Type</option>
                  <option value="quarter">Quarter</option>
                  <option value="half_yearly">Half Yearly</option>
                  <option value="9months">9 Months</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>

              {/* Current Period Filter */}
              <div className="filter-group">
                <label className="filter-label">
                  <Calendar size={12} />
                  <span>Current Period</span>
                </label>
                <select
                  value={lineItemSelectedCurrentPeriod}
                  onChange={(e) => setLineItemSelectedCurrentPeriod(e.target.value)}
                  className="filter-select"
                  disabled={!lineItemSelectedPeriodType}
                >
                  <option value="">Select Current Period</option>
                  {getLineItemCurrentPeriodOptions().map(period => (
                    <option key={period.value} value={period.value}>
                      {period.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Previous Period Filter */}
              <div className="filter-group">
                <label className="filter-label">
                  <Calendar size={12} />
                  <span>Previous Period</span>
                </label>
                <select
                  value={lineItemSelectedPreviousPeriod}
                  onChange={(e) => setLineItemSelectedPreviousPeriod(e.target.value)}
                  className="filter-select"
                  disabled={!lineItemSelectedPeriodType}
                >
                  <option value="">Select Previous Period</option>
                  {getLineItemCurrentPeriodOptions().map(period => (
                    <option key={period.value} value={period.value}>
                      {period.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Bank Filter */}
              <div className="filter-group">
                <label className="filter-label">
                  <Banknote size={12} />
                  <span>Bank</span>
                </label>
                <select
                  value={lineItemSelectedBank}
                  onChange={(e) => setLineItemSelectedBank(e.target.value)}
                  className="filter-select"
                >
                  <option value="">Select Bank</option>
                  {banks.map(bank => (
                    <option key={bank} value={bank}>
                      {bank}
                    </option>
                  ))}
                </select>
              </div>

              {/* View Type Filter */}
              <div className="filter-group">
                <label className="filter-label">
                  <TrendingUp size={12} />
                  <span>View Type</span>
                </label>
                <select
                  value={lineItemSelectedViewType}
                  onChange={(e) => setLineItemSelectedViewType(e.target.value)}
                  className="filter-select"
                >
                  <option value="actual">Actual</option>
                  <option value="qoq">QoQ</option>
                  <option value="yoy">YoY</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );

  // Center Panel Content
  const centerPanel = (
    <div className="variance-center-panel">
      {/* Global Filter - Top Right Corner */}
      <div className="global-filter-container">
        <BusinessSegmentFilter />
      </div>
      
      {!dataAvailable ? (
        <div style={{ 
          padding: '40px 20px', 
          textAlign: 'center', 
          color: '#6b7280',
          background: '#f9fafb',
          borderRadius: '8px',
          margin: '20px 0'
        }}>
          <TrendingUp size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
          <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px', color: '#374151' }}>
            Coming Soon
          </h3>
          <p style={{ fontSize: '14px', lineHeight: '1.5' }}>
            Variance analysis data is not yet available for this country. Please check back later.
          </p>
        </div>
      ) : isLoading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading variance analysis...</p>
        </div>
      ) : (
        <div className="analysis-content">
          {/* Historical Trend Component */}
          <div className="component-section">
            <div className="component-header">
              <div className="component-title">
                <TrendingUp className="component-icon" />
                Historical Trend Analysis
              </div>
              <div className="component-subtitle">Track financial metrics over time periods</div>
            </div>
            <HistoricalTrend 
              selectedBusinessSegment={selectedBusinessSegment}
              selectedBank={selectedBank}
              selectedPeriodType={selectedPeriodType}
              selectedCurrentPeriod={selectedCurrentPeriod}
              selectedCategory={selectedCategory}
              selectedFinancialItems={selectedFinancialItemsLocal}
              onUnselectItem={handleUnselectFinancialItem}
            />
          </div>

          {/* Peer Comparison Component */}
          <div className="component-section">
            <div className="component-header">
              <div className="component-title">
                <Users className="component-icon" />
                Peer Comparison Analysis
              </div>
              <div className="component-subtitle">Compare performance across multiple banks</div>
            </div>
            <ModernPeerComparison
              selectedBanks={peerSelectedBanks}
              selectedPeriodType={peerSelectedPeriodType}
              selectedCurrentPeriod={peerSelectedCurrentPeriod}
              selectedCategory={peerSelectedCategory}
              selectedItems={peerSelectedItems}
              selectedViewType={peerSelectedViewType}
              financialItems={peerFinancialItems}
            />
          </div>

          {/* Line Item Analysis Component */}
          <div className="component-section">
            <div className="component-header">
              <div className="component-title">
                <Table className="component-icon" />
                Line Item Analysis
              </div>
              <div className="component-subtitle">Detailed breakdown of financial line items</div>
            </div>
            <ModernLineItemAnalysis
              selectedBank={lineItemSelectedBank}
              selectedCategory={lineItemSelectedCategory}
              selectedViewType={lineItemSelectedViewType}
              selectedPeriodType={lineItemSelectedPeriodType}
              selectedCurrentPeriod={lineItemSelectedCurrentPeriod}
              selectedPreviousPeriod={lineItemSelectedPreviousPeriod}
              selectedColumns={lineItemSelectedColumns}
              financialItems={lineItemFinancialItems}
            />
          </div>
        </div>
      )}
    </div>
  );

  return (
    <ModernLayout 
      leftPanel={leftPanel}
      showTwoPanel={true}
    >
      {centerPanel}
    </ModernLayout>
  );
};

const ModernVarianceAnalysis = () => {
  return (
    <VarianceAppProvider>
      <ModernVarianceAnalysisContent />
    </VarianceAppProvider>
  );
};

export default ModernVarianceAnalysis;
